import { Box, Text, Flex, Textarea, Button, Skeleton } from '@chakra-ui/react'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { API_GATEWAY, SUPABASE_KEY, SUPABASE_URL } from '@/constants/constants'
import { useSession } from '@/context/SessionProvider'
import { createClient } from '@supabase/supabase-js'
import { useState, useEffect, useRef, startTransition } from 'react'
import SendIcon from '@/public/assets/icons/send-icon.svg'
import { useGetUserProfileByAddress } from '@/api/query'
import { AvatarCircle } from '@/components/atoms/Avatar'
import ChatIcon from '@/public/assets/icons/chat.svg'
import BlockedIcon from '@/public/assets/icons/blocked.svg'
import ChatHideIcon from '@/public/assets/icons/chat-hide.svg'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { useQuery } from '@tanstack/react-query'
import { useAppToast } from '@/hooks/useAppToast'
import { useSecurityApi } from '@/hooks/useSecurityApi'
import { useTranslation } from 'next-i18next/pages'

const supabase = createClient(SUPABASE_URL, SUPABASE_KEY)

const isValidMessage = (str: string) => {
  const regex = /^[~`!@#$%^&*()_+=[\]\{}|;':",.\/<>?a-zA-Z0-9- \n]+$/
  return regex.test(str)
}

export const Chat = ({ onClose }: { onClose?: () => void }) => {
  const { t } = useTranslation()
  const [messages, setMessages] = useState([]) as any[]
  const [content, setContent] = useState<string>('')
  const [isLoading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { onError } = useAppToast()
  const contentRef = useRef('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { account } = useSession()

  const { apiRequest } = useSecurityApi()

  const { data } = useQuery({
    queryKey: ['USER_CHAT_STATUS', account?.injectiveAddress],
    queryFn: () => fetch(`${API_GATEWAY}/chat/users/${account?.injectiveAddress}`).then((res) => res.json()),
    enabled: Boolean(account),
  })

  useEffect(() => {
    ref.current?.scroll(0, ref.current?.scrollHeight)
  }, [messages])

  useEffect(() => {
    contentRef.current = content
  }, [content])

  useEffect(() => {
    getMessages()

    const sub = supabase
      .channel('messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) =>
        startTransition(() => handleInsert(payload))
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, (payload) =>
        startTransition(() => handleUpdate(payload))
      )
      .subscribe()

    window.addEventListener('keydown', listener)

    return () => {
      sub.unsubscribe()
      window.removeEventListener('keydown', listener)
    }
  }, [])

  const listener = (event: KeyboardEvent) => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      sendMessage(contentRef.current)
      event.preventDefault()
    }
  }

  const handleInsert = (payload: any) => {
    setMessages((prev: any) => [...prev, payload.new])
  }

  const handleUpdate = (payload: any) => {
    setMessages((prev: any) =>
      prev.map((msg: any) => {
        if (msg.id === payload.old.id) {
          return payload.new
        }

        return msg
      })
    )
  }

  async function getMessages() {
    const { data } = await supabase
      .schema('public')
      .from('messages')
      .select('*')
      .limit(100)
      .order('id', { ascending: false })
    setMessages((data?.reverse() as any) || [])
  }

  async function sendMessage(msg: string) {
    setLoading(true)

    try {
      await apiRequest(`${API_GATEWAY}/chat/messages`, {
        room: 'en',
        message: msg,
      })
    } catch {
      onError()
    }

    setLoading(false)
    setContent('')
    textareaRef.current?.focus()
  }

  return (
    <Box pos={'relative'} pb={'80px'} pt={'64px'} w={72} h={'full'} bg={'ninja.600'} rounded={'2xl'}>
      <Box rounded={'xl'} zIndex={10} pos={'absolute'} right={2} left={2} top={0} pt={2} bg={'ninja.600'}>
        {' '}
        <Flex
          flexBasis={'full'}
          boxShadow={'0px 15px 30px 0px #0D0D2E'}
          justifyContent={'space-between'}
          p={4}
          rounded={'xl'}
          bg={'ninja.500'}
        >
          <Flex alignItems={'center'} gap={2}>
            <Box w={5}>
              <ChatIcon />
            </Box>
            <Text fontWeight={700}>{t('chat.live-chat')}</Text>
          </Flex>
          {onClose && (
            <Box onClick={onClose} cursor={'pointer'} bg={'ninja.400'} rounded={'lg'}>
              <Box
                px={2.5}
                py={2}
                w={'full'}
                h={'full'}
                opacity={0.2}
                transitionDuration={'0.2s'}
                _hover={{
                  opacity: 1,
                }}
              >
                <ChatHideIcon />
              </Box>
            </Box>
          )}
        </Flex>
      </Box>
      <Box
        ref={ref}
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
            borderRadius: '6px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#282759',
            borderRadius: '24px',
          },
        }}
        mr={1}
        pt={4}
        overflowY={'auto'}
        overscrollBehaviorY={'contain'}
        h={'full'}
      >
        {messages.length === 0 ? (
          <MessagesSkeletons />
        ) : (
          messages.map((message: any, index: any) => <Message isAdmin={data?.is_admin} key={index} message={message} />)
        )}
      </Box>
      <Box position={'absolute'} w={'full'} rounded={'xl'} bottom={0} bg={'ninja.600'}>
        <Flex h={'70px'} p={2.5} m={2} mt={0} bg={'ninja.500'} rounded={'2xl'} overflow={'hidden'}>
          {data?.is_ban ? (
            <Flex gap={2} flexBasis={'full'} justifyContent={'center'} alignItems={'center'}>
              <BlockedIcon />
              <Text color={'ninja.300'} fontSize={'sm'} fontWeight={700}>
                You're blocked
              </Text>
            </Flex>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                sendMessage(content!)
              }}
            >
              <Flex>
                <Textarea
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '4px',
                      borderRadius: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#1d1c49',
                      borderRadius: '24px',
                    },
                  }}
                  mr={1}
                  ref={textareaRef}
                  isDisabled={isLoading}
                  rows={2}
                  fontSize={'sm'}
                  fontWeight={600}
                  onChange={(e) => setContent(e.target.value)}
                  value={content}
                  placeholder={t('chat.write-message')}
                  _placeholder={{
                    color: 'ninja.300',
                  }}
                  _focus={{
                    outline: 'transparent !important',
                    border: 'none!important',
                  }}
                  _focusVisible={{
                    outline: 'transparent !important',
                    border: 'none!important',
                  }}
                  _active={{
                    outline: 'transparent !important',
                    border: 'none!important',
                  }}
                  outline={'none'}
                  border={'none'}
                  resize={'none'}
                />
                <Flex w={12} flexDir={'column'} alignItems={'end'}>
                  <Button
                    flexShrink={0}
                    isLoading={isLoading}
                    isDisabled={!content || content.length > 128 || !isValidMessage(content)}
                    w={8}
                    h={8}
                    p={0}
                    rounded={'lg'}
                    type="submit"
                  >
                    <SendIcon />
                  </Button>
                  <Text fontSize={'xs'} color={content.length > 128 ? 'brand.error' : 'ninja.200'}>
                    {content.length}/128
                  </Text>
                </Flex>
              </Flex>
            </form>
          )}
        </Flex>
      </Box>
    </Box>
  )
}

const MessagesSkeletons = () => {
  return (
    <Box>
      {Array.from({ length: 10 }).map((_, index) => (
        <MessageSkeleton key={index} index={index} />
      ))}
    </Box>
  )
}

const MessageSkeleton = ({ index }: { index: number }) => {
  return (
    <Box>
      <Flex alignItems={'end'} gap={1} ml={6} mr={6} mb={4}>
        <Skeleton flexShrink={0} startColor={'ninja.500'} endColor={'ninja.400'} w={9} h={9} rounded={'full'} />
        <Box flexBasis={'full'}>
          <Flex justifyContent={'space-between'} mb={1}>
            <Skeleton h={'18px'} startColor={'ninja.500'} w={'50px'} rounded={'md'} endColor={'ninja.400'} />
            <Skeleton h={'18px'} startColor={'ninja.500'} w={'50px'} rounded={'md'} endColor={'ninja.400'} />
          </Flex>
          <Skeleton rounded={'xl'} h={index % 3 === 0 ? 16 : 8} startColor={'ninja.500'} endColor={'ninja.400'} />
        </Box>
      </Flex>
    </Box>
  )
}

const Message = ({ message, isAdmin }: any) => {
  const { data } = useGetUserProfileByAddress(message.user)
  const numberOfNfts = data?.nfts?.totalCount
  const isNftOwner = Boolean(numberOfNfts)
  const modal = useAppModal()

  const handleOpenAdminPanel = () => {
    modal.openModalNew(AppModals.AdminPanel, { message })
  }

  const handleOpenUserProfile = () => {
    modal.openModalNew(AppModals.UserStats, { address: message.user })
  }

  if (message.is_deleted) {
    return null
  }

  return (
    <Box
      mb={4}
      _last={{
        mb: 0,
      }}
      ml={5}
      mr={4}
      pl={8}
      pos={'relative'}
    >
      <Box
        rounded={'full'}
        border={'4px solid'}
        borderColor={'ninja.600'}
        borderLeft={'none'}
        pos={'absolute'}
        bottom={-1}
        left={0}
        onClick={handleOpenUserProfile}
        cursor={'pointer'}
      >
        <AvatarCircle w={9} h={9} src={data?.user?.profileAvatar || ''} />
      </Box>

      <Flex mb={1.5} justifyContent={'space-between'}>
        {isNftOwner ? (
          <Box cursor={'pointer'} onClick={handleOpenUserProfile}>
            <GradientText
              numberOfNfts={numberOfNfts!}
              props={{
                fontWeight: 600,
                fontSize: 'xs',
              }}
            >
              {data?.user?.profileUserName}
            </GradientText>
          </Box>
        ) : (
          <Text cursor={'pointer'} onClick={handleOpenUserProfile} fontSize={'xs'} color={'ninja.100'}>
            {data?.user?.profileUserName}
          </Text>
        )}
        <Text
          fontSize={'xs'}
          cursor={isAdmin ? 'pointer' : 'auto'}
          onClick={() => (isAdmin ? handleOpenAdminPanel() : null)}
          color={'ninja.300'}
        >
          {new Date(message.created_at).toLocaleTimeString()}
        </Text>
      </Flex>
      <Box fontSize={'sm'} bg={'ninja.500'} fontWeight={500} py={2} pr={3} pl={4} rounded={'xl'}>
        {message.message}
      </Box>
    </Box>
  )
}
